// Synth bass — monophonic lowpass synth in a low octave (theremin archetype).
// Macro = post-filter cutoff (left-hand height opens/closes the tone).

import * as Tone from "tone";
import { degreeToNote } from "../util/scales.ts";
import type { NoteEvent, ScaleName } from "../util/types.ts";
import { clampVelocity, macroToCutoff, type Instrument } from "./types.ts";

const BASE_OCTAVE = 2;
const DEFAULT_MACRO = 0.6;

export function createSynthBass(): Instrument {
  let synth: Tone.MonoSynth | null = null;
  let filter: Tone.Filter | null = null;
  let key = "C";
  let scale: ScaleName = "majorPentatonic";

  const resolve = (ev: NoteEvent): string | number =>
    ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, BASE_OCTAVE) : (ev.note ?? "C2");

  return {
    id: "synth-bass",
    name: "Synth bass",
    category: "bass",
    archetype: "theremin",
    scaleLocked: true,
    polyphony: "mono",
    init(output) {
      filter = new Tone.Filter({ type: "lowpass", frequency: macroToCutoff(DEFAULT_MACRO), Q: 1 });
      filter.connect(output);
      synth = new Tone.MonoSynth({
        oscillator: { type: "triangle" },
        filter: { type: "lowpass", Q: 1 },
        filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.4, release: 0.4, baseFrequency: 120, octaves: 2.5 },
        envelope: { attack: 0.01, decay: 0.2, sustain: 0.7, release: 0.4 },
      });
      synth.connect(filter);
    },
    trigger(ev, time) {
      synth?.triggerAttackRelease(resolve(ev), ev.duration ?? "8n", time, clampVelocity(ev.velocity));
    },
    attack(ev, time) {
      synth?.triggerAttack(resolve(ev), time, clampVelocity(ev.velocity));
    },
    release(_ev, time) {
      synth?.triggerRelease(time);
    },
    setScale(k, s) {
      key = k;
      scale = s;
    },
    setMacro(v) {
      filter?.frequency.rampTo(macroToCutoff(v), 0.05);
    },
    dispose() {
      synth?.dispose();
      filter?.dispose();
      synth = null;
      filter = null;
    },
  };
}
